import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { ShoppingCart, X, CheckCircle, Calendar } from "@phosphor-icons/react";
import { minLocalScheduleString, localToISO, fmtScheduledChip, fmtScheduledAU } from "@/lib/schedule";
import { OrderHeader } from "@/components/customer_order/OrderHeader";
import { MenuBrowser } from "@/components/customer_order/MenuBrowser";
import { CartLines, CartTotals } from "@/components/customer_order/CartLines";
import { ModifierModal, openModifiers, flattenModifiers } from "@/components/customer_order/ModifierModal";
import ScheduleOrderPicker from "@/components/customer_order/ScheduleOrderPicker";
import { AiCashierLink } from "@/components/kiosk/AiCashierLink";
import { useKioskHold } from "@/components/kiosk/KioskExit";
import {
  resolvePublicHoliday,
  computePublicHolidayAmount,
  getPublicHolidayConfig,
} from "@/lib/public_holiday";

// iter_109 (2026-06-09) — Announcement display-mode storage helpers.
// Persists "seen" state in localStorage scoped by venue so customers
// at multiple venues see each independently. Stored shape:
//   { [announcement_id]: "seen" | <ISO date string> }
function _annStorageKey(venueId) {
  return `mm_announcements_seen:${venueId || "default"}`;
}
function _readAnnSeen(venueId) {
  try {
    const raw = localStorage.getItem(_annStorageKey(venueId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function _writeAnnSeen(venueId, map) {
  try { localStorage.setItem(_annStorageKey(venueId), JSON.stringify(map)); }
  catch { /* private mode etc — silent */ }
}
function _shouldShowAnn(ann, seen, todayUtcStr) {
  const m = ann.display_mode || "once_per_day";
  const stamp = seen[ann.id];
  if (!stamp) return true;
  if (m === "every_visit") return true;
  if (m === "once_per_device") return false;
  if (m === "once_per_day") return stamp !== todayUtcStr;
  return true;
}
const _ANN_TYPE_COLOURS = {
  information: { bg: "#dbeafe", color: "#1e40af", border: "#3b82f6" },
  promotion:   { bg: "#dcfce7", color: "#15803d", border: "#22c55e" },
  warning:     { bg: "#fed7aa", color: "#b45309", border: "#f97316" },
  critical:    { bg: "#fee2e2", color: "#991b1b", border: "#ef4444" },
};

// iter_107 — Pure helper. Built outside the component so the React 19
// purity lint stays happy (component body must be referentially pure).
const _DEFAULT_PAUSE_MSG =
  "Online ordering is temporarily paused. Please check back soon.";
function _buildPauseState(cfg) {
  const trimmed = typeof cfg.message === "string" ? cfg.message.trim() : "";
  return {
    active: true,
    message: trimmed || _DEFAULT_PAUSE_MSG,
    allowFuturePreorders: cfg.allow_future_preorders !== false,
    affected: cfg.affected_order_types || ["takeaway", "pickup", "delivery"],
  };
}

import { validateOrderIdentity } from "@/lib/customerOrderValidate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Counter kiosk identity for the WisePOS E endpoints: the kiosk device token, or the signed-in staff browser.
const counterAuth = () => {
  const t = localStorage.getItem("mm_kiosk_token") || localStorage.getItem("mm_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export default function CustomerOrder() {
  const [params] = useSearchParams();
  const mode = params.get("mode") || "online"; // online | qr | kiosk
  const venueId = params.get("venue") || "";
  const tableQ = params.get("table") || "";

  const [venue, setVenue] = useState(null);
  // 2026-02-23 — Delivery schedule signal from /public/menu. When false
  // the Delivery tab renders disabled with an inline message; submit
  // path still safety-rejects via backend 400 if anything slips through.
  const [deliveryOpenNow, setDeliveryOpenNow] = useState(true);
  const [deliveryMessage, setDeliveryMessage] = useState("");
  // 2026-02-27 — Machine-readable reason for delivery closure. Lets
  // the UI keep "Schedule Delivery" available when only the ASAP
  // cutoff has passed (reason_code === "cutoff_passed").
  const [deliveryReasonCode, setDeliveryReasonCode] = useState("open");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [mods, setMods] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);
  const [modOpen, setModOpen] = useState(null);
  const [orderType, setOrderType] = useState(mode === "qr" ? "dine_in" : "pickup");
  const [tableNum, setTableNum] = useState(tableQ);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // iter_139 (2026-06-13) — Kiosk Stripe EFTPOS dialog context. Set
  // when the customer chooses "Pay by Card"; the dialog polls the
  // public PI status endpoint until the webhook flips the order paid.
  const [eftposKioskCtx, setEftposKioskCtx] = useState(null);
  const kioskHold = useKioskHold({ busy: !!eftposKioskCtx, poll: mode === "kiosk" });
  // iter_139 — Kiosk EFTPOS toggle (probed from public settings).
  const [eftposKioskAvailable, setEftposKioskAvailable] = useState(false);
  const [eftposReason, setEftposReason] = useState("");
  // Slot D5 — Preorder UX: ASAP default, "scheduled" unlocks picker.
  // Mirrors backend 15-min lead rule with a UI guard; backend stays authoritative.
  const [fulfilmentType, setFulfilmentType] = useState("asap");
  const [scheduledLocal, setScheduledLocal] = useState("");
  const minLocal = useMemo(() => minLocalScheduleString(), []);

  // iter_108 (2026-06-09) — Ticking "now" used to auto-expire the
  // pause banner on the customer side. Initial Date.now() in useState
  // initializer is allowed under React 19's purity rules; the
  // setInterval lives in an effect (also allowed). The 30s cadence
  // is responsive enough for live trading without being noisy.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(i);
  }, []);

  // iter_109 (2026-06-09) — Customer announcements. Fetched from public
  // endpoint scoped by channel (qr/online/kiosk). Re-fetched every 60s
  // so scheduled windows light up automatically. POS/KDS unaffected.
  const [announcements, setAnnouncements] = useState([]);
  const [annDismissed, setAnnDismissed] = useState({});   // { id: true } for this render session
  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    const fetchAnns = async () => {
      try {
        const r = await axios.get(
          `${API}/public/announcements/${venueId}?channel=${mode}`);
        if (!cancelled) setAnnouncements(Array.isArray(r.data) ? r.data : []);
      } catch { /* silent — non-blocking */ }
    };
    fetchAnns();
    const i = setInterval(fetchAnns, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, [venueId, mode]);

  // Visible-banner list (always shown when show_banner=true and not
  // session-dismissed). The popup queue follows the display_mode rules
  // backed by localStorage.
  const bannerAnns = announcements.filter(
    (a) => a.show_banner && !annDismissed[a.id]);
  const _todayUtc = new Date(nowMs).toISOString().slice(0, 10);
  const _seenMap = venueId ? _readAnnSeen(venueId) : {};
  const popupQueue = announcements.filter((a) =>
    _shouldShowAnn(a, _seenMap, _todayUtc) && !annDismissed[a.id]);
  const activePopup = popupQueue[0] || null;

  const markAnnSeen = (a) => {
    const map = _readAnnSeen(venueId);
    map[a.id] = a.display_mode === "once_per_day" ? _todayUtc : "seen";
    _writeAnnSeen(venueId, map);
    setAnnDismissed((p) => ({ ...p, [a.id]: true }));
  };
  const dismissBanner = (a) => {
    setAnnDismissed((p) => ({ ...p, [a.id]: true }));
  };
  const runAnnButton = (a) => {
    const action = a.button_action || "close";
    const t = (a.button_target || "").trim();
    if (action === "url" && t) {
      try { window.open(t, "_blank", "noopener"); } catch { /* */ }
    } else if (action === "menu_category" && t) {
      setActiveCat(t);
    }
    // menu_item action: defer to v2 (would require scrolling into view).
    markAnnSeen(a);
  };

  // 2026-02-21 — Tender visibility for QR/Online/Kiosk. We hit the
  // public endpoint with the active channel and only render the
  // enabled tenders. On fetch failure we fall back to the legacy
  // pay_at_counter + online_card pair so customer ordering never
  // blocks on a config bug.
  const channel = mode === "kiosk" ? "kiosk" : "qr_online";
  const [tenders, setTenders] = useState(null); // null = loading
  useEffect(() => {
    if (!venueId) return;
    axios.get(`${API}/public/payment-tenders/${venueId}`, { params: { channel } })
      .then((r) => setTenders(r.data?.tenders || []))
      .catch(() => setTenders(null));
  }, [venueId, channel]);

  // iter_139 (2026-06-13) — Probe kiosk Stripe EFTPOS availability.
  // Only relevant when mode === 'kiosk'. Public endpoint never leaks
  // the reader id; it only returns {available: bool}.
  useEffect(() => {
    if (!venueId || mode !== "kiosk") { setEftposKioskAvailable(false); return; }
    axios.get(`${API}/public/stripe-terminal/settings/${venueId}`, counterAuth())
      .then((r) => { setEftposKioskAvailable(!!r.data?.available); setEftposReason(r.data?.reason || ""); })
      .catch(() => setEftposKioskAvailable(false));
  }, [venueId, mode]);

  useEffect(() => {
    if (!venueId) return;
    const fetchMenu = async () => {
      try {
        // Pass `mode` so the backend can apply channel-visibility filters
        // (visible_online for online/qr, visible_kiosk for kiosk).
        const r = await axios.get(`${API}/public/menu/${venueId}`, { params: { mode } });
        setVenue(r.data.venue); setCats(r.data.categories); setItems(r.data.items); setMods(r.data.modifier_groups);
        // 2026-02-23 — Delivery schedule flags ride along with the menu
        // payload so we never need a second fetch. Default to open if
        // the server is older / field missing (back-compat).
        setDeliveryOpenNow(r.data.delivery_open_now !== false);
        setDeliveryMessage(r.data.delivery_message || "");
        setDeliveryReasonCode(r.data.delivery_reason_code || "open");
        if (r.data.categories[0]) setActiveCat((c) => c || r.data.categories[0].id);
      } catch (e) { console.error("public menu load", e); toast.error("Could not load menu"); }
    };
    fetchMenu();
    // 2026-02-27 — Periodic refresh of delivery availability so a tab
    // left open across the open/cutoff/close boundary updates without
    // requiring a manual reload. Also refresh on tab focus.
    const refreshAvail = async () => {
      try {
        const r = await axios.get(`${API}/public/venue/${venueId}`);
        setDeliveryOpenNow(r.data.delivery_open_now !== false);
        setDeliveryMessage(r.data.delivery_message || "");
        setDeliveryReasonCode(r.data.delivery_reason_code || "open");
      } catch (e) { /* silent — never block UI on availability poll */ }
    };
    const id = setInterval(refreshAvail, 60000);
    const onFocus = () => refreshAvail();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [venueId, mode]);

  // Kiosk auto-reset after 60s of inactivity post-submission
  useEffect(() => {
    if (mode === "kiosk" && submitted) {
      const t = setTimeout(() => reset(), 12000);
      return () => clearTimeout(t);
    }
  }, [submitted, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => items.filter((i) => !activeCat || i.category_id === activeCat), [items, activeCat]);

  // iter_107 (2026-06-08) — Online ordering pause state.
  // iter_108 (2026-06-09) — Re-introduce pause_until expiry check via a
  // `nowMs` ticker (every 30s in a useEffect to keep React 19 purity
  // lint happy — Date.now() is allowed in useState initializer and
  // inside effects, just not in render body). Banner auto-disappears
  // within 30s of pause_until passing without needing a venue-settings
  // refetch. The existing 60s settings refresh still fires so owner-
  // side toggles propagate too.
  const _opCfg = venue?.settings?.online_ordering_pause || {};
  const _opUntilMs = _opCfg.pause_until ? Date.parse(_opCfg.pause_until) : NaN;
  const _opExpired = !_opCfg.pause_until_reopened
    && Number.isFinite(_opUntilMs) && _opUntilMs <= nowMs;
  const pauseState = (_opCfg.enabled && !_opExpired)
    ? _buildPauseState(_opCfg)
    : { active: false };

  // Periodic refresh so a customer page silently resumes when pause expires.
  useEffect(() => {
    if (!venueId) return;
    const i = setInterval(() => {
      axios.get(`${API}/public/venue/${venueId}`)
        .then((r) => setVenue((v) => r.data ? { ...v, ...r.data } : v))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(i);
  }, [venueId]);

  const subtotal = cart.reduce((s, i) => {
    // iter_91 — modifier price multiplied by per-modifier qty (default 1).
    const mt = i.modifiers.reduce((a, m) => a + (Number(m.price || 0) * Number(m.qty || 1)), 0);
    return s + (i.price + mt) * i.qty;
  }, 0);
  const deliveryFee = orderType === "delivery" ? (venue?.settings?.delivery_fee || 0) : 0;
  // iter_PUBLIC_HOLIDAY_SURCHARGE (2026-02-28) — Public Holiday
  // Surcharge preview. Mirrors backend: base is `subtotal` (no
  // discounts/vouchers/loyalty in the public flow yet). Public
  // orders NEVER carry a manual override — the backend ignores the
  // action for public payloads (source ∈ {online,qr,kiosk}).
  const _phCfg = getPublicHolidayConfig(venue);
  const _phResolved = resolvePublicHoliday(venue, orderType);
  const publicHolidayAmount = _phResolved.applies
    ? computePublicHolidayAmount(subtotal, _phResolved.rate)
    : 0;
  const total = subtotal + publicHolidayAmount + deliveryFee;

  const addItem = (item) => {
    // Independent concepts (do not conflate):
    //   • available === false → menu-availability OFF (cannot order). Show
    //     a disabled "Currently unavailable" card.
    //   • sold_out === true   → in-stock-but-out-now. Show "Sold out"
    //     overlay; still cannot add to cart.
    if (item.available === false) return toast.error("Currently unavailable");
    if (item.sold_out) return toast.error("Sold out");
    if (item.modifier_group_ids?.length) {
      // Item's configured modifier_group_ids order (Menu Builder), same as POS.
      setModOpen(openModifiers(item, mods));
    } else {
      setCart((c) => [...c, { menu_item_id: item.id, name: item.name, price: item.price, qty: 1, notes: "", modifiers: [], kds_station: item.kds_station, _key: Math.random().toString(36) }]);
    }
  };
  const confirmMods = () => {
    const { item } = modOpen;
    const { error, flat } = flattenModifiers(modOpen);
    if (error) return toast.error(error);
    setCart((c) => [...c, { menu_item_id: item.id, name: item.name, price: item.price, qty: 1, notes: "", modifiers: flat, kds_station: item.kds_station, _key: Math.random().toString(36) }]);
    setModOpen(null);
  };

  const reset = () => {
    setCart([]); setName(""); setPhone(""); setAddress(""); setNotes(""); setTableNum(tableQ); setSubmitted(null); setOpen(false);
    setOrderType(mode === "qr" ? "dine_in" : "pickup");
    // Slot D5 — reset preorder UI.
    setFulfilmentType("asap"); setScheduledLocal("");
  };

  const submit = async (payment_method = "pay_at_counter", extras = {}) => {
    if (!cart.length) return toast.error("Cart is empty");
    // iter_125 (2026-06-12) — P0 PAYMENT INTEGRITY HARDENING. Allow-list
    // the payment_method at the frontend boundary so a buggy callsite
    // can never POST a POS-only tender to /api/public/orders. Backend
    // also enforces this (defence in depth).
    const _PUBLIC_PAYMENT_METHODS = new Set([
      "pay_at_counter", "pay_driver", "online_card", "stripe_eftpos_kiosk",
    ]);
    if (!_PUBLIC_PAYMENT_METHODS.has(payment_method)) {
      return toast.error("Payment method is not available for online ordering.");
    }
    // 2026-02-28 (iter_83) — Identity guard. Mirrors the backend
    // validator so we don't lose the cart on a 400. QR with table number
    // is exempt per spec (table is enough); QR without table falls back
    // to the takeaway-style name+phone requirement.
    const idCheck = validateOrderIdentity({
      orderType,
      fulfilmentType,
      tableNumber: tableNum,
      customerName: name,
      customerPhone: phone,
      deliveryAddress: orderType === "delivery" ? address : null,
      source: mode === "qr" ? "qr" : mode === "kiosk" ? "kiosk" : "online",
    });
    if (!idCheck.ok) return toast.error(idCheck.error);
    // iter_107 client-side guard removed — backend returns HTTP 409 with
    // the owner's custom message and the error toast surfaces it (see
    // catch branch below). Removing the up-front guard keeps the React
    // 19 purity lint clean (Date.now() ops inside submit stay isolated).
    // Legacy guards retained for clarity (validator covers these too).
    if (orderType === "delivery" && !address) return toast.error("Address required for delivery");
    if (orderType === "dine_in" && !tableNum && mode !== "kiosk" && mode !== "qr") return toast.error("Table number required");
    // Slot D5 — client-side preorder guard. Server stays authoritative.
    let scheduledISO = null;
    if (fulfilmentType === "scheduled") {
      if (!scheduledLocal) return toast.error("Pick a preorder time first");
      scheduledISO = localToISO(scheduledLocal);
      if (!scheduledISO) return toast.error("Invalid preorder time");
      if (new Date(scheduledISO).getTime() < Date.now() + 15 * 60 * 1000) {
        return toast.error("Preorder must be at least 15 minutes from now");
      }
      // iter_211 (2026-02-28) — Pre-checkout slot revalidation. Hit
      // the public slots endpoint for the selected calendar date and
      // confirm the chosen ISO is still on the list. If not, show
      // the spec-mandated error and block the submit. The backend
      // will also reject via the venue-hours validator so this is
      // defence-in-depth, not the source of truth.
      const localDate = scheduledLocal.slice(0, 10); // YYYY-MM-DD
      try {
        const src = mode === "qr" ? "qr" : mode === "kiosk" ? "kiosk" : "online";
        const r = await axios.get(
          `${API}/public/ordering/slots/${venueId}`,
          { params: { order_type: orderType, source: src, date: localDate } });
        const slots = (r?.data?.slots) || [];
        // iter_RESTORE_HOTFIX (2026-02-28, P0) — Revert iter_211's
        // "empty slots → toast" branch. For venues that haven't
        // configured `venue_hours`, the slots endpoint legitimately
        // returns [] for every date (legacy back-compat mode) — the
        // backend itself accepts those preorders. iter_211 tightened
        // this so empty slots blocked submit, which broke Preorder
        // Delivery on production venues. Restore the original
        // semantics: only block when slots are populated but our
        // chosen ISO is missing; defer empty-slot cases to the
        // backend's canonical validator (which is a no-op for
        // unconfigured venues).
        if (slots.length > 0) {
          const isoToLocal = (iso) => {
            const d = new Date(iso);
            const p = (n) => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
          };
          const stillThere = slots.some(
            (iso) => isoToLocal(iso) === scheduledLocal);
          if (!stillThere) {
            return toast.error(
              "This time is no longer available. Please choose another available time.");
          }
        }
      } catch (e) {
        // Network blip — don't block the customer; backend is the
        // ultimate gate.
      }
    }
    setSubmitting(true);
    // iter_125 — sanitise extras: strip POS-only payment mechanics so a
    // future caller passing tenders/tendered/tip_amount/credit/loyalty
    // can never leak them into a public order POST. Whitelist the
    // delivery-specific keys we DO want to accept.
    const _PUBLIC_EXTRAS_ALLOWED = new Set([
      "delivery_payment_mode", "driver_cash_tendered",
    ]);
    const safeExtras = Object.fromEntries(
      Object.entries(extras || {})
        .filter(([k]) => _PUBLIC_EXTRAS_ALLOWED.has(k))
    );
    const orderBody = {
      venue_id: venueId, source: mode, order_type: orderType,
      items: cart.map((i) => ({ menu_item_id: i.menu_item_id, name: i.name, price: i.price, qty: i.qty, notes: i.notes, modifiers: i.modifiers, kds_station: i.kds_station })),
      customer: (name || phone) ? { name, phone, address } : null,
      table_number: tableNum || null,
      notes, payment_method,
      // Slot D5 — Preorder fields. ASAP sends null so server stamps default.
      fulfilment_type: fulfilmentType,
      scheduled_for: scheduledISO,
      // 2026-02-23 — Mirror the POS payload contract for delivery so the
      // backend lift logic populates delivery_address / delivery_phone /
      // delivery_notes top-level columns the driver app reads. Without
      // this blob, customer-online delivery orders left those columns
      // null and the Delivery card rendered blank rows.
      delivery: orderType === "delivery"
        ? { address: address || null, phone: phone || null, notes: notes || null }
        : null,
      // 2026-02-23 — Delivery cash-on-arrival path. Caller (DeliveryPaymentBlock)
      // forwards `delivery_payment_mode` + optional `driver_cash_tendered`
      // here. Backend already validates and computes `driver_change`.
      ...safeExtras,
    };
    try {
      if (payment_method === "online_card") {
        // iter_ONLINE_DEDUPE (2026-02-28) — Generate ONE token per
        // checkout-attempt life (kept in sessionStorage). Resubmissions
        // (browser back / double-click / multi-tab) reuse the same
        // token so the backend can supersede the earlier unpaid order
        // and never leave a paid+abandoned duplicate pair behind.
        let clientToken = null;
        try {
          clientToken = sessionStorage.getItem("mm_online_token");
          if (!clientToken) {
            clientToken = (window.crypto?.randomUUID?.()
              || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            sessionStorage.setItem("mm_online_token", clientToken);
          }
        } catch (_e) { clientToken = null; }
        const r = await axios.post(`${API}/public/payments/stripe/checkout`, {
          order: orderBody, origin_url: window.location.origin,
          client_token: clientToken,
        });
        window.location.href = r.data.checkout_url;
        return;
      }
      // iter_139 (2026-06-13) — Kiosk Stripe EFTPOS. Dedicated public
      // endpoint creates an unpaid order + PaymentIntent + pushes to
      // the venue's default smart reader. We then poll status until
      // the webhook flips the order to paid and assigns a ticket.
      if (payment_method === "stripe_eftpos_kiosk") {
        const r = await axios.post(`${API}/public/stripe-terminal/start`, {
          venue_id: venueId, order: orderBody,
        }, counterAuth());
        setEftposKioskCtx({
          orderId: r.data.order_id,
          paymentIntentId: r.data.payment_intent_id,
          readerId: r.data.reader_id,
          amount: Number(total || 0),
          testMode: r.data.test_mode,
        });
        setOpen(false);
        return;
      }
      const r = await axios.post(`${API}/public/orders`, orderBody);
      setSubmitted(r.data);
      setOpen(false);
    } catch (e) {
      // iter_RESTORE_HOTFIX (2026-02-28, P0) — Render-crash guard.
      // The backend returns `detail` in three shapes:
      //   (a) plain string  → safe to toast directly
      //   (b) structured envelope {code, message, operator_message, …}
      //       (Stripe Terminal / business errors) → MUST extract a string
      //   (c) array of Pydantic validation errors [{loc, msg, type}, …]
      //       (422 responses) → MUST extract the first msg
      // Without this guard, `toast.error(detail)` passes an object/array
      // to sonner which renders it as a React child and trips the global
      // ErrorBoundary with "Objects are not valid as a React child".
      // That's the exact production crash reported on 2026-02-28.
      const d = e?.response?.data?.detail;
      let msg = "Failed";
      if (typeof d === "string") {
        msg = d;
      } else if (Array.isArray(d)) {
        // Pydantic 422 errors. Surface the first one.
        const first = d[0];
        msg = first?.msg || first?.message || "Validation failed";
      } else if (d && typeof d === "object") {
        msg = d.operator_message || d.message || d.code || "Failed";
      } else if (e?.message) {
        msg = e.message;
      }
      toast.error(String(msg));
      // eslint-disable-next-line no-console
      console.warn("[customer_order] submit failed", d || e);
    } finally { setSubmitting(false); }
  };

  if (!venueId) {
    return <div className="min-h-screen flex items-center justify-center p-6"><div className="text-center text-sm text-[var(--ink-500)]">Missing <code>?venue=</code> parameter.</div></div>;
  }
  if (!venue) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--ink-500)]">Loading menu…</div>;
  }

  // iter_139 (2026-06-13) — Kiosk Stripe EFTPOS dialog. Polls the
  // public PI status endpoint; when the webhook flips the order paid
  // and assigns a ticket number, we set `submitted` to surface the
  // success view (same as other tenders) and clear the dialog ctx.
  const eftposKioskDialog = eftposKioskCtx ? (
    <KioskEftposDialog
      ctx={eftposKioskCtx}
      venueId={venueId}
      total={total}
      onCancel={async () => {
        try {
          await axios.post(`${API}/public/stripe-terminal/cancel`,
            { venue_id: venueId, payment_intent_id: eftposKioskCtx.paymentIntentId }, counterAuth()
          ).catch(() => {});
        } finally {
          setEftposKioskCtx(null);
        }
      }}
      onPaid={(orderData) => {
        setSubmitted(orderData);
        setEftposKioskCtx(null);
      }}
      onClose={() => { setEftposKioskCtx(null); setOpen(true); }}
    />
  ) : null;

  // SUCCESS view
  if (submitted) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 ${mode === "kiosk" ? "bg-[var(--sand-50)]" : ""}`}>
        <div className="text-center max-w-md fade-up">
          <div className="w-20 h-20 rounded-full bg-[var(--moss-bg)] flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={40} weight="fill" className="text-[var(--moss)]" />
          </div>
          <div className="label-eyebrow mb-2">Order placed</div>
          <div className="font-heading text-5xl font-semibold tabular text-[var(--terracotta)]">#{submitted.ticket_number}</div>
          <p className="text-[var(--ink-700)] mt-4">
            Your order is on the way to the kitchen. {orderType === "dine_in" ? `We'll bring it to ${tableNum ? `table ${tableNum}` : "your table"}.` : "We'll let you know when it's ready."}
          </p>
          <p className="text-xs text-[var(--ink-500)] mt-3 tabular">
            {orderType === "delivery"
              ? `Driver will collect cash on arrival · ${fmtMoney(submitted.total)}`
              : submitted.paid_by_card ? `Paid by card · ${fmtMoney(submitted.total)}` : `Pay at counter · ${fmtMoney(submitted.total)}`}
          </p>
          <button data-testid="order-done" onClick={reset} className="mt-8 h-12 px-8 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] font-medium active:scale-[0.98]">
            {mode === "kiosk" ? "Start new order" : "Done"}
          </button>
        </div>
      </div>
    );
  }

  const isKiosk = mode === "kiosk";
  return (
    <div className={`min-h-screen ${isKiosk ? "bg-[var(--sand-50)]" : "bg-[var(--sand-50)]"}`}>
      {eftposKioskDialog}
      {isKiosk && kioskHold.overlay}
      {/* Header */}
      <OrderHeader isKiosk={isKiosk} mode={mode} venueName={venue.name} logoBind={isKiosk ? kioskHold.bind : {}}>
        {isKiosk && <AiCashierLink />}
        {!isKiosk && (
          <button data-testid="open-cart" onClick={() => setOpen(true)} className="relative px-4 h-10 rounded-full bg-[var(--ink-900)] text-[var(--sand-50)] text-sm font-medium flex items-center gap-2 active:scale-95">
            <ShoppingCart size={16} /> {cart.length} · <span className="tabular">{fmtMoney(total)}</span>
          </button>
        )}
      </OrderHeader>

      {/* iter_107 (2026-06-08) — Online ordering pause banner. Shows
          across the whole page until owner reopens. ASAP checkout is
          gated in submit(); banner offers a Preorder shortcut when
          allow_future_preorders is on. */}
      {pauseState.active && pauseState.affected.includes(orderType) && (
        <div data-testid="op-banner"
             className="bg-[var(--destructive)] text-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="max-w-[1300px] mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-90">
                Online ordering temporarily paused
              </div>
              <div data-testid="op-banner-message" className="text-sm sm:text-base mt-0.5">
                {pauseState.message}
              </div>
            </div>
            {pauseState.allowFuturePreorders && fulfilmentType !== "scheduled" && (
              <button
                type="button"
                data-testid="op-preorder-btn"
                onClick={() => {
                  setFulfilmentType("scheduled");
                  if (!scheduledLocal) setScheduledLocal(minLocal);
                  setOpen(true);
                }}
                className="self-start sm:self-auto h-9 px-4 rounded-md bg-white text-[var(--destructive)] text-sm font-semibold hover:opacity-90 active:scale-95 inline-flex items-center gap-1.5"
              >
                <Calendar size={14} weight="bold" /> Choose Preorder Time
              </button>
            )}
          </div>
        </div>
      )}

      {/* iter_109 (2026-06-09) — Customer Announcement banners. Stack
          below the pause banner, type-coloured left border, dismissible
          per-session. Popup is rendered at the bottom of this fragment. */}
      {bannerAnns.map((a) => {
        const col = _ANN_TYPE_COLOURS[a.type] || _ANN_TYPE_COLOURS.information;
        return (
          <div key={a.id}
               data-testid={`ann-banner-${a.id}`}
               className="px-4 py-2 sm:px-6 sm:py-2.5"
               style={{ background: col.bg, color: col.color, borderLeft: `4px solid ${col.border}` }}>
            <div className="max-w-[1300px] mx-auto flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">{a.type}</div>
                <div className="text-sm sm:text-base font-medium truncate">{a.title}</div>
                <div className="text-xs opacity-90 whitespace-pre-line line-clamp-2">{a.message}</div>
              </div>
              <button data-testid={`ann-banner-close-${a.id}`}
                      onClick={() => dismissBanner(a)}
                      className="text-xs uppercase tracking-wide opacity-70 hover:opacity-100 shrink-0">
                ✕
              </button>
            </div>
          </div>
        );
      })}

      <div className={`max-w-[1300px] mx-auto ${isKiosk ? "p-8" : "p-4"} ${isKiosk ? "grid grid-cols-12 gap-8" : ""}`}>
        <div className={isKiosk ? "col-span-8" : ""}>
          {/* Category tabs — rows of 5 (4 on kiosk lg, 5 on kiosk xl) instead
              of a horizontal scroller. Matches POS layout for staff/customer
              consistency. */}
          <MenuBrowser cats={cats} activeCat={activeCat} setActiveCat={setActiveCat} items={filtered} isKiosk={isKiosk} onAdd={addItem} />
        </div>

        {/* Kiosk cart pinned right */}
        {isKiosk && (
          <aside className="col-span-4 sticky top-32 self-start bg-white border border-[rgba(28,25,23,0.08)] rounded-xl p-5 max-h-[calc(100vh-9rem)] overflow-y-auto" data-testid="kiosk-cart">
            <CartContent
              cart={cart} setCart={setCart} total={total} subtotal={subtotal} deliveryFee={deliveryFee}
              publicHolidayAmount={publicHolidayAmount} publicHoliday={_phResolved} publicHolidayCfg={_phCfg}
              orderType={orderType} setOrderType={setOrderType} mode={mode} venue={venue}
              tableNum={tableNum} setTableNum={setTableNum}
              name={name} setName={setName} phone={phone} setPhone={setPhone} address={address} setAddress={setAddress}
              notes={notes} setNotes={setNotes}
              submitting={submitting} submit={submit}
              fulfilmentType={fulfilmentType} setFulfilmentType={setFulfilmentType}
              scheduledLocal={scheduledLocal} setScheduledLocal={setScheduledLocal} minLocal={minLocal}
              tenders={tenders}
              deliveryOpenNow={deliveryOpenNow} deliveryMessage={deliveryMessage}
              deliveryReasonCode={deliveryReasonCode}
              eftposKioskAvailable={eftposKioskAvailable} eftposReason={eftposReason}
            />
          </aside>
        )}
      </div>

      {/* Mobile floating cart */}
      {!isKiosk && cart.length > 0 && !open && (
        <button data-testid="floating-cart" onClick={() => setOpen(true)} className="fixed bottom-4 left-4 right-4 h-14 rounded-full bg-[var(--ink-900)] text-[var(--sand-50)] flex items-center justify-between px-6 shadow-lg z-40 active:scale-[0.99]">
          <span className="text-sm font-medium">View Cart · {cart.length} items</span>
          <span className="tabular font-medium">{fmtMoney(total)}</span>
        </button>
      )}

      {/* Online/QR full-screen checkout sheet */}
      {!isKiosk && open && (
        <div className="fixed inset-0 bg-[var(--sand-50)] z-50 overflow-y-auto">
          <div className="max-w-md mx-auto p-4 pb-32">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl font-semibold">Your order</h3>
              <button onClick={() => setOpen(false)} data-testid="close-cart"><X size={20} /></button>
            </div>
            <CartContent
              cart={cart} setCart={setCart} total={total} subtotal={subtotal} deliveryFee={deliveryFee}
              publicHolidayAmount={publicHolidayAmount} publicHoliday={_phResolved} publicHolidayCfg={_phCfg}
              orderType={orderType} setOrderType={setOrderType} mode={mode} venue={venue}
              tableNum={tableNum} setTableNum={setTableNum}
              name={name} setName={setName} phone={phone} setPhone={setPhone} address={address} setAddress={setAddress}
              notes={notes} setNotes={setNotes}
              submitting={submitting} submit={submit}
              fulfilmentType={fulfilmentType} setFulfilmentType={setFulfilmentType}
              scheduledLocal={scheduledLocal} setScheduledLocal={setScheduledLocal} minLocal={minLocal}
              tenders={tenders}
              deliveryOpenNow={deliveryOpenNow} deliveryMessage={deliveryMessage}
              deliveryReasonCode={deliveryReasonCode}
              eftposKioskAvailable={eftposKioskAvailable} eftposReason={eftposReason}
            />
          </div>
        </div>
      )}

      {/* Modifier modal */}
      {modOpen && <ModifierModal modOpen={modOpen} setModOpen={setModOpen} onConfirm={confirmMods} />}

      {/* iter_109 (2026-06-09) — Customer Announcement popup. One at a
          time. Acknowledgement-required popups gate the close action. */}
      {activePopup && (
        <AnnouncementPopup
          ann={activePopup}
          onAcknowledge={() => markAnnSeen(activePopup)}
          onButton={() => runAnnButton(activePopup)}
        />
      )}
    </div>
  );
}

function AnnouncementPopup({ ann, onAcknowledge, onButton }) {
  const col = _ANN_TYPE_COLOURS[ann.type] || _ANN_TYPE_COLOURS.information;
  const requireAck = !!ann.require_acknowledgement;
  const [acked, setAcked] = useState(false);
  const canClose = !requireAck || acked;
  const close = () => { if (canClose) onAcknowledge(); };
  const buttonText = (ann.button_text || "").trim();
  const showButton = !!buttonText && ann.button_action && ann.button_action !== "close";
  return (
    <div
      data-testid={`ann-popup-${ann.id}`}
      data-ann-type={ann.type}
      className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={canClose ? close : undefined}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ borderTop: `6px solid ${col.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 flex items-center justify-between"
             style={{ background: col.bg, color: col.color }}>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">{ann.type}</div>
          {canClose ? (
            <button
              data-testid={`ann-popup-close-${ann.id}`}
              onClick={close}
              className="text-sm opacity-70 hover:opacity-100"
              aria-label="Close announcement"
            >
              <X size={18} />
            </button>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.18em] opacity-70">Ack required</span>
          )}
        </div>
        {ann.image_url ? (
          <img
            src={ann.image_url}
            alt=""
            data-testid={`ann-popup-image-${ann.id}`}
            className="w-full max-h-64 object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : null}
        <div className="px-5 py-4">
          <h3 className="text-xl font-heading font-semibold leading-tight"
              data-testid={`ann-popup-title-${ann.id}`}>
            {ann.title}
          </h3>
          {ann.message ? (
            <p className="text-sm text-[var(--ink-700)] mt-2 whitespace-pre-line"
               data-testid={`ann-popup-message-${ann.id}`}>
              {ann.message}
            </p>
          ) : null}
          {requireAck && (
            <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer select-none"
                   data-testid={`ann-popup-ack-row-${ann.id}`}>
              <input
                type="checkbox"
                data-testid={`ann-popup-ack-${ann.id}`}
                checked={acked}
                onChange={(e) => setAcked(e.target.checked)}
              />
              I understand
            </label>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[rgba(28,25,23,0.08)] flex items-center justify-end gap-2">
          {showButton && (
            <button
              data-testid={`ann-popup-action-${ann.id}`}
              disabled={requireAck && !acked}
              onClick={() => { if (!requireAck || acked) onButton(); }}
              className="h-10 px-4 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: col.border, color: "#fff" }}
            >
              {buttonText}
            </button>
          )}
          <button
            data-testid={`ann-popup-dismiss-${ann.id}`}
            disabled={!canClose}
            onClick={close}
            className="h-10 px-4 rounded-md border border-[rgba(28,25,23,0.18)] text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {requireAck ? "Acknowledge & continue" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CartContent(props) {
  const { cart, setCart, total, subtotal, deliveryFee, publicHolidayAmount = 0, publicHoliday = null, publicHolidayCfg = null, orderType, setOrderType, mode, venue, tableNum, setTableNum, name, setName, phone, setPhone, address, setAddress, notes, setNotes, submitting, submit, fulfilmentType, setFulfilmentType, scheduledLocal, setScheduledLocal, minLocal, tenders, deliveryOpenNow = true, deliveryMessage = "", deliveryReasonCode = "open", eftposKioskAvailable = false, eftposReason = "" } = props;

  // iter_RESTORE_HOTFIX_DELIVERY_PREORDER (2026-02-28, P0) —
  // Availability signals only gate ASAP delivery. In Preorder mode
  // the customer is scheduling a FUTURE delivery window, so ASAP
  // closure (any reason) must NEVER block the Delivery tab itself.
  // Backend remains authoritative at checkout time and will reject
  // a scheduled delivery only if it's genuinely outside the venue's
  // configured ordering windows. Venues without `venue_hours`
  // configured fall through to "open" semantics in the backend
  // validator — no client-side gating required.
  const deliveryAsapClosed = !deliveryOpenNow;
  // "Schedulable" cutoff-style closure auto-flips Preorder on for
  // the customer when they tap the Delivery tab — same UX as before
  // iter_RESTORE_HOTFIX_DELIVERY_PREORDER, just generalised.
  const deliveryAutoSwitchToPreorder =
    deliveryAsapClosed && deliveryReasonCode === "cutoff_passed";

  // Voice-of-customer reason text for the inline ASAP-only banner.
  // Suppressed in scheduled mode because Preorder is still valid.
  const showAsapUnavailableNotice =
    orderType === "delivery"
    && fulfilmentType === "asap"
    && deliveryAsapClosed
    && !!deliveryMessage;

  // 2026-02-21 — Map tender keys to in-page submit actions. Only these
  // two payment_method strings are wired today; other enabled tenders
  // render a disabled stub explaining the gap.
  const tenderKeyToAction = {
    pay_at_counter: { pay: "pay_at_counter", testid: "co-submit", primary: false },
    stripe_online:  { pay: "online_card",    testid: "co-submit-online", primary: true },
    // iter_139 (2026-06-13) — Kiosk Stripe EFTPOS. Dynamic tender key
    // surfaced when the venue has enabled kiosk EFTPOS + a default
    // reader. Backed by the new /api/public/stripe-terminal/start
    // endpoint (not /api/public/orders) so it's strictly isolated
    // from iter_125's public allow-list.
    stripe_eftpos:  { pay: "stripe_eftpos_kiosk", testid: "co-submit-eftpos", primary: true },
  };

  const inc = (k) => setCart((c) => c.map((i) => i._key === k ? { ...i, qty: i.qty + 1 } : i));
  const dec = (k) => setCart((c) => c.map((i) => i._key === k ? { ...i, qty: Math.max(1, i.qty - 1) } : i));
  const remove = (k) => setCart((c) => c.filter((i) => i._key !== k));
  const s = venue?.settings || {};
  const types = mode === "qr"
    ? ["dine_in"]
    : (mode === "kiosk" ? ["dine_in", "takeaway"] : ["pickup", ...(s.delivery_enabled ? ["delivery"] : [])]);

  return (
    <>
      <div className="flex gap-2 mb-2">
        {types.map((t) => {
          // iter_RESTORE_HOTFIX_DELIVERY_PREORDER (2026-02-28, P0) —
          // The Delivery tab is ALWAYS clickable when delivery_enabled
          // is true. ASAP closure never disables the tab itself; the
          // ASAP toggle below carries the gating instead, and the
          // backend remains authoritative at checkout time. This
          // restores Preorder Delivery for venues without `venue_hours`
          // configured and for venues that are currently closed for
          // ASAP but accept future windows.
          return (
            <button key={t} data-testid={`co-type-${t}`}
              onClick={() => {
                setOrderType(t);
                // Auto-flip to scheduled mode when ASAP is cutoff-blocked
                // so the customer immediately sees the date/time picker.
                if (t === "delivery" && deliveryAutoSwitchToPreorder) {
                  setFulfilmentType("scheduled");
                }
              }}
              className={`pill-tab ${orderType === t ? "pill-tab-active" : "pill-tab-inactive"}`}>
              {t.replace("_", " ")}
            </button>
          );
        })}
      </div>
      {/* iter_RESTORE_HOTFIX_DELIVERY_PREORDER (2026-02-28, P0) —
          Inline ASAP-unavailable notice. Hidden in scheduled mode
          because Preorder remains a valid path. Backend enforces
          the same gate at checkout (defence in depth). */}
      {showAsapUnavailableNotice && (
        <div data-testid="co-delivery-unavailable"
             className="mb-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 leading-relaxed">
          {deliveryMessage}
        </div>
      )}

      {/* Slot D5 — ASAP / Preorder toggle. Inline picker only renders when
          scheduled is selected. Backend validates ≥15 min lead.
          2026-02-27 — ASAP disabled for delivery when cutoff has passed;
          customer is forced to Preorder for a future window. */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          data-testid="co-fulfilment-asap"
          onClick={() => { setFulfilmentType("asap"); setScheduledLocal(""); }}
          disabled={orderType === "delivery" && deliveryAsapClosed}
          title={orderType === "delivery" && deliveryAsapClosed
            ? (deliveryMessage || "ASAP delivery currently unavailable — please preorder a future window.")
            : ""}
          className={`flex-1 h-10 rounded-md text-xs uppercase tracking-wider font-medium active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${fulfilmentType === "asap" ? "bg-[var(--ink-900)] text-[var(--sand-50)]" : "bg-white border border-[rgba(28,25,23,0.12)] text-[var(--ink-700)]"}`}
        >ASAP</button>
        <button
          type="button"
          data-testid="co-fulfilment-scheduled"
          onClick={() => {
            setFulfilmentType("scheduled");
            if (!scheduledLocal) setScheduledLocal(minLocal);
          }}
          className={`flex-1 h-10 rounded-md text-xs uppercase tracking-wider font-medium inline-flex items-center justify-center gap-1 active:scale-95 ${fulfilmentType === "scheduled" ? "bg-[var(--ink-900)] text-[var(--sand-50)]" : "bg-white border border-[rgba(28,25,23,0.12)] text-[var(--ink-700)]"}`}
        ><Calendar size={12} weight="bold" /> Preorder</button>
      </div>
      {fulfilmentType === "scheduled" && (
        <div className="mb-3 space-y-1" data-testid="co-scheduled-block">
          {/* iter_211 (2026-02-28) — Smart calendar + slot picker.
              Backend is the only source of truth for valid days/times.
              The native datetime-local control has been removed per
              spec. The hidden mirror keeps a `co-scheduled-input`
              data-testid alive so existing automation that asserts on
              it (e.g. set value programmatically) still finds a node;
              its value is filled by the picker. */}
          <input
            type="hidden" data-testid="co-scheduled-input"
            value={scheduledLocal} readOnly
          />
          <ScheduleOrderPicker
            venueId={venue?.id || venue?._id}
            orderType={orderType}
            source={mode === "qr" ? "qr" : mode === "kiosk" ? "kiosk" : "online"}
            value={scheduledLocal}
            onChange={(v) => setScheduledLocal(v)}
            onAvailabilityChange={(isOpen) => {
              // If ASAP is genuinely unavailable, the parent UI already
              // forced Preorder on. Nothing else to do here — the
              // picker simply ensures the customer cannot pick an
              // invalid time.
              void isOpen;
            }}
          />
          {scheduledLocal && (
            <div
              data-testid="co-scheduled-au-chip"
              className="text-[12px] text-[var(--ink-700)] tabular pt-1"
            >
              You picked: <span className="font-medium">{fmtScheduledAU(localToISO(scheduledLocal))}</span>
            </div>
          )}
        </div>
      )}

      <CartLines
        lines={cart.map((i) => ({ key: i._key, name: i.name, modifiers: i.modifiers, qty: i.qty,
          // modifier subtotal multiplied by per-modifier qty
          total: (i.price + i.modifiers.reduce((a, m) => a + (Number(m.price || 0) * Number(m.qty || 1)), 0)) * i.qty }))}
        onInc={inc} onDec={dec} onRemove={remove} />

      {orderType === "dine_in" && (
        <input data-testid="co-table" value={tableNum} onChange={(e) => setTableNum(e.target.value)} placeholder="Table #" className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] mb-2" />
      )}
      <input data-testid="co-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] mb-2" />
      <input data-testid="co-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (for loyalty & SMS)" className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] mb-2" />
      {orderType === "delivery" && (
        <input data-testid="co-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery address" className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] mb-2" />
      )}
      <textarea data-testid="co-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Order notes…" className="w-full px-3 py-2 rounded-md border border-[rgba(28,25,23,0.12)] mb-3 text-sm" />

      <CartTotals subtotal={subtotal} publicHoliday={publicHoliday} publicHolidayAmount={publicHolidayAmount} deliveryFee={deliveryFee} total={total} />
      {/* 2026-02-23 — Delivery orders must NOT show "Pay at counter"
          (customer never visits the counter). Render the dedicated
          delivery payment block instead; pickup/dine-in/etc. keep the
          dynamic tender list below. */}
      {orderType === "delivery" ? (
        <DeliveryPaymentBlock total={total} tenders={tenders}
                              submitting={submitting} cart={cart}
                              submit={submit} />
      ) : (() => {
        // 2026-02-21 — Dynamic tender list. Falls back to the legacy
        // pay_at_counter + online_card pair if the public endpoint
        // returns null. Only known tender keys render; unknown keys
        // render a disabled stub so admins notice the gap.
        const legacy = [
          { key: "pay_at_counter", label: "Pay at counter" },
          { key: "stripe_online",  label: "Pay online" },
        ];
        let list = tenders || legacy;
        // The counter kiosk never uses Stripe online checkout — card = the WisePOS E terminal (backend enforces too).
        if (mode === "kiosk") list = list.filter((t) => t.key !== "stripe_online" && t.key !== "eftpos");
        const counterNote = mode === "kiosk" && !eftposKioskAvailable && eftposReason ? (
          <div key="eftpos-note" data-testid="co-eftpos-unavailable" className="text-[11px] text-[var(--ink-500)] text-center mt-2">
            {eftposReason === "not_counter" ? "Card terminal: a manager needs to switch this screen to Kiosk mode (staff login → Device mode) to take card payments here."
              : "Card terminal isn't set up for this venue (Settings → Payments → Stripe Terminal)."}
          </div>) : null;
        // iter_139 (2026-06-13) — Kiosk-only: prepend the Stripe EFTPOS
        // option when the venue has enabled kiosk EFTPOS + has a default
        // reader configured. Probed independently from the standard
        // tender visibility endpoint.
        if (mode === "kiosk" && eftposKioskAvailable
            && !list.some((t) => t.key === "stripe_eftpos")) {
          list = [{ key: "stripe_eftpos", label: "Pay by Card" }, ...list];
        }
        if (list.length === 0) {
          return (
            <div data-testid="co-pay-empty"
                 className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-3 mt-4 text-xs text-amber-900 text-center">
              No payment methods enabled for this channel. Ask staff to enable a tender in Settings &rarr; Payments.
            </div>
          );
        }
        return [...list.map((t, i) => {
          const action = tenderKeyToAction[t.key];
          if (!action) {
            return (
              <button key={t.key} disabled data-testid={`co-pay-${t.key}-unsupported`}
                      className="w-full h-12 mt-2 rounded-md border border-[rgba(28,25,23,0.12)] text-[var(--ink-500)] text-sm">
                {t.label} (not available on this channel yet)
              </button>
            );
          }
          const isPrimary = action.primary || i === 0;
          return (
            <button
              key={t.key}
              data-testid={action.testid}
              onClick={() => submit(action.pay)}
              disabled={submitting || !cart.length}
              className={`w-full h-12 mt-${i === 0 ? "4" : "2"} rounded-md ${isPrimary ? "bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] text-white" : "bg-[var(--ink-900)] hover:bg-black text-[var(--sand-50)]"} font-medium disabled:opacity-50 active:scale-[0.98]`}
            >
              {submitting ? (action.pay === "online_card" ? "Redirecting..." : "Sending...") : `${t.label} - ${fmtMoney(total)}`}
            </button>
          );
        }), counterNote];
      })()}
    </>
  );
}

// 2026-02-23 — Delivery checkout payment block. Replaces the generic
// tender list for delivery orders only. Customer NEVER sees "Pay at
// counter" (they're not visiting). Pay-online (Stripe) is offered only
// when the venue has the `stripe_online` tender enabled for the
// QR/Online channel. The two driver-cash buttons map to the existing
// backend contract (delivery_payment_mode + driver_cash_tendered) — no
// backend change required.
function DeliveryPaymentBlock({ total, tenders, submitting, cart, submit }) {
  const [changeOpen, setChangeOpen] = useState(false);
  const [cashTendered, setCashTendered] = useState("");

  // AU 5c rounding mirrors backend `round_au_5c`. Server stays authoritative.
  const roundAu5c = (amount) => {
    const cents = Math.round((Number(amount) || 0) * 100);
    const mod = cents % 5;
    const rc = mod < 3 ? cents - mod : cents + (5 - mod);
    return Math.round(rc) / 100;
  };
  const doorTotal = roundAu5c(total);
  const t = Number(cashTendered);
  const tendered = Number.isFinite(t) ? t : 0;
  const change = Math.max(tendered - doorTotal, 0);
  const under = tendered > 0 && tendered < doorTotal;

  // Pay-online is gated on the venue's tender config — same contract as
  // before, just isolated to this block.
  // iter_RESTORE_HOTFIX_DELIVERY_PREORDER (2026-02-28, P0 follow-up) —
  // When the `/public/payment-tenders` endpoint is unreachable (network
  // blip / config not yet seeded) the parent sets `tenders = null`.
  // The Pickup block already mirrors that to the legacy
  // `[pay_at_counter, stripe_online]` pair so Pay Online keeps working.
  // The Delivery block previously coerced null → [] which silently
  // hid Pay Online. Apply the same legacy fallback here so Delivery
  // never loses Pay Online relative to Pickup. When the endpoint DID
  // succeed and explicitly omitted stripe_online, the venue has
  // genuinely disabled it and we honour that — Pay Online stays
  // hidden for Pickup AND Delivery (identical behaviour).
  const stripeEnabled = tenders == null
    ? true
    : tenders.some(
        (x) => x.key === "stripe_online" && x.enabled !== false);

  const disabled = submitting || !cart.length;

  return (
    <div className="mt-4 space-y-2" data-testid="co-delivery-payment">
      {stripeEnabled && (
        <button
          data-testid="co-pay-online"
          disabled={disabled}
          onClick={() => submit("online_card",
            { delivery_payment_mode: "online_card" })}
          className="w-full h-12 rounded-md bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] text-white font-medium disabled:opacity-50 active:scale-[0.98]"
        >
          {submitting ? "Redirecting..." : `Pay Online (card) - ${fmtMoney(total)}`}
        </button>
      )}

      <button
        data-testid="co-pay-driver-cash"
        disabled={disabled}
        onClick={() => submit("pay_at_counter",
          { delivery_payment_mode: "pay_driver" })}
        className="w-full h-12 rounded-md bg-[var(--ink-900)] hover:bg-black text-[var(--sand-50)] font-medium disabled:opacity-50 active:scale-[0.98]"
      >
        {submitting ? "Sending..." : `Driver Collect Cash - ${fmtMoney(total)}`}
      </button>

      <button
        data-testid="co-pay-driver-cash-change-toggle"
        disabled={disabled}
        onClick={() => setChangeOpen((v) => !v)}
        className="w-full h-12 rounded-md border border-[rgba(28,25,23,0.12)] hover:bg-[var(--sand-100)] text-[var(--ink-900)] text-sm font-medium disabled:opacity-50 active:scale-[0.98]"
      >
        Driver Collect Cash (Change Required)
      </button>

      {changeOpen && (
        <div className="border border-[rgba(28,25,23,0.12)] rounded-md p-3 space-y-2"
             data-testid="co-driver-change-panel">
          <label htmlFor="co-cash-tendered" className="text-[10px] uppercase tracking-wider text-[var(--ink-500)]">
            Cash customer will pay with
          </label>
          <input id="co-cash-tendered" data-testid="co-cash-tendered"
            type="number" inputMode="decimal" step="0.05" min="0"
            value={cashTendered}
            onChange={(e) => setCashTendered(e.target.value)}
            placeholder={`$${doorTotal.toFixed(2)}`}
            className="w-full h-11 px-3 text-sm rounded-md border border-[rgba(28,25,23,0.12)] tabular text-right" />
          <div className="bg-[var(--sand-100)]/60 rounded-md px-3 py-2 text-xs tabular space-y-0.5">
            <div className="flex justify-between"><span className="text-[var(--ink-500)]">Door total</span><span>${doorTotal.toFixed(2)}</span></div>
            <div className={`flex justify-between font-medium ${under ? "text-[var(--terracotta)]" : ""}`}>
              <span>Driver provides change</span>
              <span data-testid="co-driver-change-amount">{under ? "—" : `$${change.toFixed(2)}`}</span>
            </div>
          </div>
          {under && (
            <p className="text-[11px] text-[var(--terracotta)]" data-testid="co-cash-tendered-under">
              Cash amount must be at least the door total.
            </p>
          )}
          <button
            data-testid="co-pay-driver-cash-change-submit"
            disabled={disabled || under || tendered <= 0}
            onClick={() => submit("pay_at_counter", {
              delivery_payment_mode: "pay_driver",
              driver_cash_tendered: Number(tendered.toFixed(2)),
            })}
            className="w-full h-11 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50 active:scale-[0.98]"
          >
            {submitting ? "Sending..." : `Confirm — Driver brings $${change.toFixed(2)} change`}
          </button>
        </div>
      )}
    </div>
  );
}

// iter_139 (2026-06-13) — Kiosk Stripe EFTPOS dialog.
//
// Polls /api/public/stripe-terminal/status/{pi_id} every 1.5s. When
// the backend webhook flips the order to paid (and a ticket is
// assigned), we surface the success view via onPaid().
function KioskEftposDialog({ ctx, venueId, total, onCancel, onPaid, onClose }) {
  const [phase, setPhase] = useState("processing");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (phase !== "processing") return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/public/stripe-terminal/status/${ctx.paymentIntentId}`,
          { params: { venue_id: venueId }, ...counterAuth() });
        if (!alive) return;
        setInfo(r.data || null);
        if (["declined", "cancelled"].includes(r.data?.state)) { setPhase(r.data.state); return; }
        if (r.data?.order_payment_status === "paid"
            && r.data?.ticket_number) {
          setPhase("approved");
          setTimeout(() => {
            onPaid?.({
              id: r.data.order_id,
              ticket_number: r.data.ticket_number,
              total: total,
              paid_by_card: true,
            });
          }, 1200);
        }
      } catch (_e) { /* network blip — keep polling */ }
    };
    tick();
    const t = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(t); };
  }, [phase, ctx.paymentIntentId, venueId, total, onPaid]);

  return (
    <div data-testid="kiosk-eftpos-dialog" data-phase={phase}
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center space-y-4">
        {phase === "processing" && (
          <>
            <div data-testid="kiosk-eftpos-processing"
                  className="text-2xl font-heading">
              Please tap your card on the payment terminal
            </div>
            <div className="text-4xl tabular text-[var(--terracotta)]">
              {fmtMoney(total)}
            </div>
            {ctx.testMode && (
              <div data-testid="kiosk-eftpos-test-badge"
                    className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                Test mode
              </div>
            )}
            <button
              data-testid="kiosk-eftpos-cancel"
              onClick={onCancel}
              className="mt-2 h-10 px-6 rounded-md bg-stone-200 text-stone-700 text-sm">
              Cancel
            </button>
          </>
        )}
        {(phase === "declined" || phase === "cancelled") && (
          <div data-testid="kiosk-eftpos-failed" className="space-y-3">
            <div className="text-2xl font-heading text-[var(--terracotta)]">
              {phase === "declined" ? "Card declined" : "Payment cancelled"}
            </div>
            <div className="text-sm text-stone-600">You haven't been charged. Your cart is still here — try again or choose Pay at counter.</div>
            <button data-testid="kiosk-eftpos-back" onClick={onClose}
              className="mt-2 h-11 px-6 rounded-md bg-[var(--ink-900)] text-white text-sm">Back to my order</button>
          </div>
        )}
        {phase === "approved" && (
          <div data-testid="kiosk-eftpos-approved" className="space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={32} weight="fill" className="text-emerald-600" />
            </div>
            <div className="text-2xl font-heading text-emerald-700">
              Payment approved
            </div>
            {info?.ticket_number && (
              <div className="text-sm text-stone-500">
                Order #{info.ticket_number}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

